import React from 'react';

import AuthMethodButtons from '@components/AuthMethodButtons/AuthMethodButtons';
import Button from '@components/Button';

import { AuthScreen, useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { useAgencyFlags } from '@private-crm-app/hooks/useAgencyFlags';

import './LoginChoice.scss';

interface LoginChoiceProps {
  screen?: AuthScreen;
  mode?: 'signin' | 'signup';
  onClose?: () => void;
}

const LoginChoice: React.FC<LoginChoiceProps> = ({ screen = AuthScreen.LISTING, mode = 'signin', onClose }) => {
  const { login, loginWithPhone, loginWithApple, loginWithGoogle, signup, signupWithPhone } = useAuthManagerContext();
  const { flags } = useAgencyFlags();

  const isSignup = mode === 'signup';
  const isAppleSignInEnabled =
    flags.isAccountsFeatureFlagEnabled && flags.isMobileAppEnabled && !!flags.appleAuth0ConnectionName;
  const authMethodCopy = isAppleSignInEnabled ? 'Email, Phone, Google, or Apple' : 'Email, Phone, or Google';

  const content = isSignup
    ? { title: 'Create Your Account', subtitle: `Use ${authMethodCopy} to get started.` }
    : { title: 'Choose a Sign In Method', subtitle: `Use ${authMethodCopy} to sign in.` };

  const handleEmail = () => (isSignup ? signup(screen) : login(screen));
  const handlePhone = () => (isSignup ? signupWithPhone(screen) : loginWithPhone(screen));
  const handleApple = () => loginWithApple(screen, flags.appleAuth0ConnectionName || undefined);
  const handleGoogle = () => loginWithGoogle(screen);

  return (
    <main className="login-choice">
      <div className="login-choice__container">
        {onClose && (
          <button type="button" className="login-choice__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
        <div className="login-choice__header">
          <h1 className="login-choice__title">{content.title}</h1>
          <p className="login-choice__subtitle">{content.subtitle}</p>
        </div>

        <div className="login-choice__buttons">
          <Button
            onClick={handleEmail}
            bsStyle={'primary' as ButtonStyle}
            content="Continue with Email"
            className="login-choice__button login-choice__button--email"
          />

          <div className="login-choice__divider">
            <span>OR</span>
          </div>

          <AuthMethodButtons
            onPhone={handlePhone}
            onGoogle={handleGoogle}
            onApple={handleApple}
            showApple={isAppleSignInEnabled}
          />
        </div>
      </div>
    </main>
  );
};

export default LoginChoice;
